import { browser } from "$app/environment";
import { goto } from "$app/navigation";
import { accessTokenExpiresAtMs, userFromAccessToken } from "$lib/auth/jwt";
import { UserRole, parseUserRole, type UserRole as Role } from "$lib/auth/roles";
import type { AuthUser } from "$lib/auth/types";
import { env } from "$lib/config/env";
import { createGraphqlClient, GraphqlError } from "$lib/graphql/client";
import {
  IdentityAcceptInviteDocument,
  IdentityChangePasswordDocument,
  IdentityCompleteOauthLoginDocument,
  IdentityRequestPasswordResetDocument,
  IdentityResetPasswordDocument,
  IdentityGoogleLoginEligibleDocument,
  IdentityLoginDocument,
  IdentityLoginWithGoogleDocument,
  IdentityLoginWithMetaDocument,
  IdentityLogoutDocument,
  IdentityMeDocument,
  IdentityMetaLoginEligibleDocument,
  IdentityRefreshDocument,
  IdentityStartGoogleLoginDocument,
  IdentityStartMetaLoginDocument,
  type IdentityAcceptInviteMutation,
  type IdentityChangePasswordMutation,
  type IdentityCompleteOauthLoginMutation,
  type IdentityRequestPasswordResetMutation,
  type IdentityResetPasswordMutation,
  type IdentityGoogleLoginEligibleQuery,
  type IdentityLoginMutation,
  type IdentityLoginWithGoogleMutation,
  type IdentityLoginWithMetaMutation,
  type IdentityLogoutMutation,
  type IdentityMeQuery,
  type IdentityMetaLoginEligibleQuery,
  type IdentityRefreshMutation,
  type IdentityStartGoogleLoginMutation,
  type IdentityStartMetaLoginMutation,
} from "$lib/graphql/generated/graphql";
import { mutationSuccess } from "$lib/graphql/payload";
import { isTotpRequired } from "$lib/graphql/resource.svelte";
import * as m from "$lib/paraglide/messages";

export type { AuthUser };

const MOCK_STORAGE_KEY = "maxxus_auth_mock";
const REFRESH_STORAGE_KEY = "maxxus_refresh_token";
const REFRESH_EXPIRES_STORAGE_KEY = "maxxus_refresh_expires_at";
const REFRESH_SKEW_MS = 60_000;

let token = $state<string | null>(null);
let user = $state<AuthUser | null>(null);
let loading = $state(true);
let resolved = $state(false);
let accessExpiresAtMs = $state<number | null>(null);
let refreshTimer: ReturnType<typeof setTimeout> | null = null;
let initPromise: Promise<void> | null = null;
let refreshInFlight: Promise<boolean> | null = null;
/** Bumped on login/logout so stale refresh attempts cannot clobber a newer session. */
let authEpoch = 0;
let storageListenerRegistered = false;

function anonymousClient() {
  return createGraphqlClient();
}

function bumpAuthEpoch(): number {
  authEpoch += 1;
  return authEpoch;
}

function isStaleAuthOperation(epochAtStart: number): boolean {
  return epochAtStart !== authEpoch;
}

function applySession(
  next: { token: string; user: AuthUser; accessExpiresAtMs?: number | null } | null,
): void {
  clearRefreshTimer();
  if (next) {
    token = next.token;
    user = next.user;
    accessExpiresAtMs = next.accessExpiresAtMs ?? accessTokenExpiresAtMs(next.token) ?? null;
    scheduleRefresh();
  } else {
    token = null;
    user = null;
    accessExpiresAtMs = null;
  }
  loading = false;
  resolved = true;
}

function clearRefreshTimer(): void {
  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }
}

function scheduleRefresh(): void {
  if (!browser || env.authMock || !accessExpiresAtMs) return;
  clearRefreshTimer();
  const delay = Math.max(accessExpiresAtMs - Date.now() - REFRESH_SKEW_MS, 5_000);
  refreshTimer = setTimeout(() => {
    void auth.refreshSession().catch(() => {
      /* logout handled inside refreshSession */
    });
  }, delay);
}

function readRefreshToken(): string | null {
  if (!browser) return null;
  try {
    return localStorage.getItem(REFRESH_STORAGE_KEY);
  } catch {
    return null;
  }
}

function readRefreshExpiresAtMs(): number | null {
  if (!browser) return null;
  try {
    const raw = localStorage.getItem(REFRESH_EXPIRES_STORAGE_KEY);
    if (!raw) return null;
    const ms = Number(raw);
    return Number.isFinite(ms) ? ms : null;
  } catch {
    return null;
  }
}

function isStoredRefreshTokenExpired(): boolean {
  const expiresAtMs = readRefreshExpiresAtMs();
  return expiresAtMs !== null && expiresAtMs <= Date.now();
}

function clearPersistedRefresh(): void {
  persistRefreshToken(null);
  persistRefreshExpiresAt(null);
}

function persistRefreshToken(refreshToken: string | null): void {
  if (!browser) return;
  try {
    if (refreshToken) localStorage.setItem(REFRESH_STORAGE_KEY, refreshToken);
    else localStorage.removeItem(REFRESH_STORAGE_KEY);
  } catch {
    /* private mode / quota */
  }
}

function persistRefreshExpiresAt(refreshExpiresAt: string | null): void {
  if (!browser) return;
  try {
    if (!refreshExpiresAt) {
      localStorage.removeItem(REFRESH_EXPIRES_STORAGE_KEY);
      return;
    }
    const ms = Date.parse(refreshExpiresAt);
    if (Number.isFinite(ms)) localStorage.setItem(REFRESH_EXPIRES_STORAGE_KEY, String(ms));
  } catch {
    /* private mode / quota */
  }
}

function persistMock(session: { token: string; user: AuthUser } | null): void {
  if (!browser || !env.authMock) return;
  if (session) localStorage.setItem(MOCK_STORAGE_KEY, JSON.stringify(session));
  else localStorage.removeItem(MOCK_STORAGE_KEY);
}

function readMock(): { token: string; user: AuthUser } | null {
  if (!browser) return null;
  try {
    const raw = localStorage.getItem(MOCK_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { token: string; user: AuthUser & { role: unknown } };
    const role = parseUserRole(parsed.user.role) ?? UserRole.BROKER;
    return { token: parsed.token, user: { ...parsed.user, role } };
  } catch {
    return null;
  }
}

function mockRoleFromEmail(email: string): Role {
  const local = email.split("@")[0]?.toLowerCase() ?? "";
  if (local.includes("admin")) return UserRole.ORGANIZATION_ADMIN;
  if (local.includes("lead") || local.includes("vedouci")) return UserRole.TEAM_LEAD;
  if (local.includes("external") || local.includes("ext")) return UserRole.EXTERNAL;
  if (local.includes("assist")) return UserRole.ASSISTANT;
  return UserRole.BROKER;
}

function userFromMe(me: IdentityMeQuery["identity"]["me"]): AuthUser {
  return {
    id: me.id,
    email: me.loginEmail,
    firstName: me.firstName,
    lastName: me.lastName,
    role: parseUserRole(me.role) ?? UserRole.BROKER,
    avatarUrl: me.avatarUrl,
    publicSlug: me.publicSlug,
    publicTitle: me.publicTitle,
    publicEmail: me.publicEmail,
    publicPhone: me.publicPhone,
    publicBio: me.publicBio,
    showPublicProfile: me.showPublicProfile,
    mustResetPassword: Boolean(me.mustResetPassword),
  };
}

async function hydrateMe(accessToken: string): Promise<AuthUser | null> {
  try {
    const data = await createGraphqlClient({ accessToken }).request<IdentityMeQuery>(
      IdentityMeDocument,
    );
    return userFromMe(data.identity.me);
  } catch {
    return userFromAccessToken(accessToken);
  }
}

async function sessionFromAuthPayload(
  payload:
    | IdentityLoginMutation["identity"]["login"]
    | IdentityRefreshMutation["identity"]["refresh"]
    | IdentityAcceptInviteMutation["identity"]["acceptInvite"]
    | IdentityLoginWithGoogleMutation["identity"]["loginWithGoogle"]
    | IdentityLoginWithMetaMutation["identity"]["loginWithMeta"]
    | IdentityCompleteOauthLoginMutation["identity"]["completeOauthLogin"]
    | IdentityChangePasswordMutation["identity"]["changePassword"],
): Promise<{ token: string; user: AuthUser; accessExpiresAtMs: number }> {
  const success = mutationSuccess(payload);
  persistRefreshToken(success.refreshToken);
  persistRefreshExpiresAt(success.refreshExpiresAt);

  const hydrated =
    (await hydrateMe(success.accessToken)) ?? userFromAccessToken(success.accessToken);
  if (!hydrated) throw new Error("Neplatný access token z API");
  const nextUser =
    success.mustResetPassword || hydrated.mustResetPassword
      ? { ...hydrated, mustResetPassword: true }
      : hydrated;

  const expires = Date.parse(success.accessExpiresAt);
  return {
    token: success.accessToken,
    user: nextUser,
    accessExpiresAtMs: Number.isFinite(expires)
      ? expires
      : (accessTokenExpiresAtMs(success.accessToken) ?? Date.now() + 15 * 60_000),
  };
}

function isDefinitiveRefreshFailure(err: unknown): boolean {
  if (err instanceof GraphqlError) {
    if (err.isUnauthorized) return true;
    const topCode = err.code ?? "";
    if (/TOKEN_REVOKED|TOKEN_REUSED|REFRESH.*REUSE/i.test(topCode)) return true;
    return err.errors.some((e) => {
      const code = typeof e.extensions?.code === "string" ? e.extensions.code : "";
      if (/TOKEN_REVOKED|TOKEN_REUSED|REFRESH.*REUSE/i.test(code)) return true;
      return /invalid.*refresh|refresh.*(expired|revoked|invalid|reused)|token.*revoked|refresh token reuse/i.test(
        e.message,
      );
    });
  }
  if (err instanceof Error && err.message.includes("Neplatný access token")) return true;
  return false;
}

function authErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof GraphqlError) return err.errors[0]?.message ?? err.message ?? fallback;
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

function registerStorageListener(): void {
  if (!browser || storageListenerRegistered || env.authMock) return;
  storageListenerRegistered = true;

  window.addEventListener("storage", (event) => {
    if (event.key !== REFRESH_STORAGE_KEY) return;
    if (event.newValue) {
      if (!token) void auth.init();
      return;
    }
    if (token) void auth.logout();
  });
}

async function bootstrapFromStorage(): Promise<void> {
  if (!browser) {
    applySession(null);
    return;
  }

  if (env.authMock) {
    applySession(readMock());
    return;
  }

  try {
    localStorage.removeItem(MOCK_STORAGE_KEY);
  } catch {
    /* ignore */
  }

  registerStorageListener();

  const refreshToken = readRefreshToken();
  if (!refreshToken || isStoredRefreshTokenExpired()) {
    clearPersistedRefresh();
    applySession(null);
    return;
  }

  const ok = await auth.refreshSession();
  if (!ok && !token) applySession(null);
}

export const auth = {
  get token(): string | null {
    return token;
  },
  get user(): AuthUser | null {
    return user;
  },
  get loading(): boolean {
    return loading;
  },
  get resolved(): boolean {
    return resolved;
  },
  get isAuthenticated(): boolean {
    return Boolean(token && user);
  },
  get role(): Role | null {
    return user?.role ?? null;
  },
  /** Org-level administration. `ADMIN` is a legacy platform role, not used for gating. */
  get isAdmin(): boolean {
    return user?.role === UserRole.ORGANIZATION_ADMIN;
  },
  get isOrganizationAdmin(): boolean {
    return user?.role === UserRole.ORGANIZATION_ADMIN;
  },
  get isFranchiseManager(): boolean {
    return user?.role === UserRole.FRANCHISE_MANAGER;
  },
  get client() {
    return createGraphqlClient({
      getAccessToken: () => token,
      onUnauthorized: () => auth.refreshSession(),
    });
  },
  init(): Promise<void> {
    if (initPromise) return initPromise;

    loading = true;
    resolved = false;

    initPromise = bootstrapFromStorage().finally(() => {
      initPromise = null;
    });

    return initPromise;
  },
  async refreshSession(explicitRefreshToken?: string): Promise<boolean> {
    if (env.authMock) return Boolean(token && user);
    if (refreshInFlight) return refreshInFlight;

    const epochAtStart = authEpoch;

    refreshInFlight = (async () => {
      const refreshToken = explicitRefreshToken ?? readRefreshToken();
      if (!refreshToken || isStoredRefreshTokenExpired()) {
        if (isStaleAuthOperation(epochAtStart)) return Boolean(token);
        clearPersistedRefresh();
        applySession(null);
        return false;
      }

      try {
        const data = await anonymousClient().request<IdentityRefreshMutation>(
          IdentityRefreshDocument,
          {
            input: { refreshToken },
          },
        );

        if (isStaleAuthOperation(epochAtStart)) return Boolean(token);

        applySession(await sessionFromAuthPayload(data.identity.refresh));
        return true;
      } catch (err) {
        if (isStaleAuthOperation(epochAtStart)) return Boolean(token);

        if (isDefinitiveRefreshFailure(err)) {
          clearPersistedRefresh();
          applySession(null);
        } else {
          // Transient failure (network, 5xx): keep refresh token for the next attempt.
          if (!token) applySession(null);
        }
        return false;
      } finally {
        refreshInFlight = null;
      }
    })();

    return refreshInFlight;
  },
  async login(
    email: string,
    password: string,
    organizationSlug?: string,
    totpCode?: string,
  ): Promise<void> {
    bumpAuthEpoch();

    if (env.authMock) {
      const role = mockRoleFromEmail(email);
      const session = {
        token: `mock.${btoa(email)}.${Date.now()}`,
        user: {
          id: "mock-user",
          email,
          firstName: role === UserRole.ORGANIZATION_ADMIN ? "Admin" : "Makléř",
          lastName: "Mock",
          role,
        } satisfies AuthUser,
      };
      persistMock(session);
      applySession(session);
      return;
    }

    try {
      const data = await anonymousClient().request<IdentityLoginMutation>(IdentityLoginDocument, {
        input: {
          loginEmail: email.trim(),
          password,
          organizationSlug: organizationSlug?.trim() || undefined,
          ...(totpCode?.trim() ? { totpCode: totpCode.trim() } : {}),
        },
      });
      applySession(await sessionFromAuthPayload(data.identity.login));
    } catch (err) {
      if (isTotpRequired(err)) throw err;
      throw new Error(authErrorMessage(err, "Přihlášení se nezdařilo"));
    }
  },
  async acceptInvite(token: string, password: string): Promise<void> {
    bumpAuthEpoch();

    if (env.authMock) {
      throw new Error("Invite accept is not available in mock auth mode");
    }

    try {
      const data = await anonymousClient().request<IdentityAcceptInviteMutation>(
        IdentityAcceptInviteDocument,
        { token: token.trim(), password },
      );
      applySession(await sessionFromAuthPayload(data.identity.acceptInvite));
    } catch (err) {
      throw new Error(authErrorMessage(err, "Pozvánka je neplatná nebo vypršela"));
    }
  },
  async loginWithGoogle(code: string, state: string, totpCode?: string): Promise<void> {
    bumpAuthEpoch();

    if (env.authMock) {
      throw new Error("Google sign-in is not available in mock auth mode");
    }

    try {
      const data = await anonymousClient().request<IdentityLoginWithGoogleMutation>(
        IdentityLoginWithGoogleDocument,
        {
          code,
          state,
          ...(totpCode?.trim() ? { totpCode: totpCode.trim() } : {}),
        },
      );
      applySession(await sessionFromAuthPayload(data.identity.loginWithGoogle));
    } catch (err) {
      if (isTotpRequired(err)) throw err;
      throw new Error(authErrorMessage(err, "Google přihlášení se nezdařilo"));
    }
  },
  async startGoogleLogin(options: {
    organizationSlug?: string;
    returnTo?: string;
    loginEmail?: string;
  }): Promise<string | null> {
    if (env.authMock) return null;
    const loginEmail = options.loginEmail?.trim() || undefined;
    if (loginEmail) {
      const check = await anonymousClient().request<IdentityGoogleLoginEligibleQuery>(
        IdentityGoogleLoginEligibleDocument,
        {
          loginEmail,
          organizationSlug: options.organizationSlug?.trim() || undefined,
        },
      );
      if (!check.identity.googleLoginEligible) {
        throw new Error(m.auth_google_not_linked());
      }
    }
    const data = await anonymousClient().request<IdentityStartGoogleLoginMutation>(
      IdentityStartGoogleLoginDocument,
      {
        organizationSlug: options.organizationSlug?.trim() || undefined,
        returnTo: options.returnTo,
      },
    );
    const start = mutationSuccess(data.identity.startGoogleLogin);
    if (start.oauthStart.stub || !start.oauthStart.authorizationUrl) {
      throw new Error(m.auth_google_not_configured());
    }
    return start.oauthStart.authorizationUrl;
  },
  async loginWithMeta(code: string, state: string, totpCode?: string): Promise<void> {
    bumpAuthEpoch();

    if (env.authMock) {
      throw new Error("Meta sign-in is not available in mock auth mode");
    }

    try {
      const data = await anonymousClient().request<IdentityLoginWithMetaMutation>(
        IdentityLoginWithMetaDocument,
        {
          code,
          state,
          ...(totpCode?.trim() ? { totpCode: totpCode.trim() } : {}),
        },
      );
      applySession(await sessionFromAuthPayload(data.identity.loginWithMeta));
    } catch (err) {
      if (isTotpRequired(err)) throw err;
      throw new Error(authErrorMessage(err, "Meta přihlášení se nezdařilo"));
    }
  },
  async completeOauthLogin(pendingToken: string, totpCode: string): Promise<void> {
    bumpAuthEpoch();

    if (env.authMock) {
      throw new Error("OAuth login is not available in mock auth mode");
    }

    try {
      const data = await anonymousClient().request<IdentityCompleteOauthLoginMutation>(
        IdentityCompleteOauthLoginDocument,
        { pendingToken, totpCode: totpCode.trim() },
      );
      applySession(await sessionFromAuthPayload(data.identity.completeOauthLogin));
    } catch (err) {
      throw new Error(authErrorMessage(err, "Ověření se nezdařilo"));
    }
  },
  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    if (env.authMock) {
      throw new Error("Change password is not available in mock auth mode");
    }

    bumpAuthEpoch();

    try {
      const data = await auth.client.request<IdentityChangePasswordMutation>(
        IdentityChangePasswordDocument,
        { input: { currentPassword, newPassword } },
      );
      applySession(await sessionFromAuthPayload(data.identity.changePassword));
    } catch (err) {
      throw new Error(authErrorMessage(err, "Změna hesla se nezdařila"));
    }
  },
  async startMetaLogin(options: {
    organizationSlug?: string;
    returnTo?: string;
    loginEmail?: string;
  }): Promise<string | null> {
    if (env.authMock) return null;
    const loginEmail = options.loginEmail?.trim() || undefined;
    if (loginEmail) {
      const check = await anonymousClient().request<IdentityMetaLoginEligibleQuery>(
        IdentityMetaLoginEligibleDocument,
        {
          loginEmail,
          organizationSlug: options.organizationSlug?.trim() || undefined,
        },
      );
      if (!check.identity.metaLoginEligible) {
        throw new Error(m.auth_meta_not_linked());
      }
    }
    const data = await anonymousClient().request<IdentityStartMetaLoginMutation>(
      IdentityStartMetaLoginDocument,
      {
        organizationSlug: options.organizationSlug?.trim() || undefined,
        returnTo: options.returnTo,
      },
    );
    const start = mutationSuccess(data.identity.startMetaLogin);
    if (start.oauthStart.stub || !start.oauthStart.authorizationUrl) {
      throw new Error(m.auth_meta_not_configured());
    }
    return start.oauthStart.authorizationUrl;
  },
  async reloadMe(): Promise<void> {
    if (!token || env.authMock) return;
    const next = await hydrateMe(token);
    if (next) user = next;
  },
  async logout(options: { redirectTo?: string } = {}): Promise<void> {
    bumpAuthEpoch();
    clearRefreshTimer();

    const refreshToken = readRefreshToken();
    if (!env.authMock && refreshToken) {
      try {
        const client = token
          ? createGraphqlClient({ accessToken: token })
          : anonymousClient();
        await client.request<IdentityLogoutMutation>(IdentityLogoutDocument, {
          refreshToken,
          allSessions: false,
        });
      } catch {
        /* best-effort server logout */
      }
    }

    clearPersistedRefresh();
    persistMock(null);
    applySession(null);
    if (browser && options.redirectTo) {
      await goto(options.redirectTo, { replaceState: true });
    }
  },

  async requestPasswordReset(email: string, organizationSlug?: string): Promise<string> {
    if (env.authMock) {
      throw new Error("Password reset is not available in mock auth mode");
    }
    try {
      const data = await anonymousClient().request<IdentityRequestPasswordResetMutation>(
        IdentityRequestPasswordResetDocument,
        {
          input: {
            loginEmail: email.trim(),
            organizationSlug: organizationSlug?.trim() || undefined,
          },
        },
      );
      const success = mutationSuccess(data.identity.requestPasswordReset);
      return typeof success.message === "string" ? success.message : "";
    } catch (err) {
      if (err instanceof GraphqlError) throw err;
      throw new Error(authErrorMessage(err, "Password reset request failed"));
    }
  },
  async resetPassword(token: string, newPassword: string): Promise<string> {
    if (env.authMock) {
      throw new Error("Password reset is not available in mock auth mode");
    }
    try {
      const data = await anonymousClient().request<IdentityResetPasswordMutation>(
        IdentityResetPasswordDocument,
        { input: { token: token.trim(), newPassword } },
      );
      const success = mutationSuccess(data.identity.resetPassword);
      return typeof success.message === "string" ? success.message : "";
    } catch (err) {
      if (err instanceof GraphqlError) throw err;
      throw new Error(authErrorMessage(err, "Password reset failed"));
    }
  },
  async logoutAndRedirect(path = "/login"): Promise<void> {
    await auth.logout({ redirectTo: path });
  },
};
